import { Col, Row } from '@advanza/grid'
import { Button, Divider, PreIcon, Toggle } from '@advanza/ui'
import { onFilesRejected } from 'actions/files'
import { invalidateJwtForUser, invalidateSessionForUser, saveUser } from 'actions/users'
import clsx from 'clsx'
import AvatarEdit from 'components/AvatarEdit'
import CheckButton from 'components/CheckButton'
import LinearLoader from 'components/LinearLoader'
import AutoRow from 'components/misc/AutoRow'
import { SSO_BADGE, SSO_ICON, SSO_LABEL } from 'components/misc/ssoIcons'
import Restricted from 'components/Restricted'
import EntityComponent from 'components/services/EntityComponent'
import FormattedTime from 'components/ui/FormattedTime'
import EmailAddressEdit from 'components/users/EmailAddressEdit'
import UsersTags from 'components/users/UsersTags'
import EntityComponentContainer from 'containers/EntityComponentContainer'
import { isSuperUser } from 'misc/user'
import { Fragment } from 'react'
import isEmail from 'validator/es/lib/isEmail'
import style from './userEntity.module.css'

class UserEntity extends EntityComponent {
    constructor(props) {
        super(props)
        this.getSetting = this.getSetting.bind(this)
        this.mobilePhoneIsValid = true
        this.onChangeAvatar = this.onChangeAvatar.bind(this)
        this.onCrop = this.onCrop.bind(this)
        this.options = {
            store: props.useStore,
            relations: [{ relationField: 'email_addresses' }],
        }
        this.emailAddressValidates = {}
        this.notificationSettings = new Map()
    }

    mobilePhoneValidator = (val) => !val || this.mobilePhoneIsValid
    textValidator = (val) => val && val.length > 0

    editFields() {
        const { country_code } = this.props.entity
        return {
            gender: {
                type: 'select',
                className: 'select-min select-stealth fsize lg',
                options: [
                    { value: 1, title: 'Mr' },
                    { value: 2, title: 'Mrs' },
                    { value: 9, title: 'Mx' },
                ],
                validator: (val) => val === 1 || val === 2 || val === 0 || val === 9,
            },
            first_name: { type: 'text', msg: 'First Name' },
            last_name: { type: 'text', msg: 'Last name' },
            email: { type: 'email', validator: isEmail, msg: 'Email' },
            email_notifications: {
                type: 'email',
                validator: (val) => !val || isEmail(val),
                msg: 'Email Notifications',
            },
            phone: { type: 'phone', msg: 'Phone' },
            phone_notifications_display: {
                type: 'mobilePhone',
                defaultCountry: country_code.toString().toLowerCase() || 'nl',
                msg: '',
                validationCallback: (isValid) => {
                    this.mobilePhoneIsValid = isValid
                },
                validator: this.mobilePhoneValidator,
            },
            fc_whatsapp_enabled: {
                type: 'boolean',
                msg: 'WhatsApp',
            },
            country_code: {
                type: 'select',
                className: 'select-min fsize lg',
                placeholder: 'Country',
                options: [
                    { value: 'NL', title: 'NL' },
                    { value: 'BE', title: 'BE' },
                    { value: 'AT', title: 'AT' },
                ],
            },
            active: {
                type: 'boolean',
                reverse: true,
                msg: 'Disabled',
                negative: true,
            },
            username: {
                type: 'text',
                placeholder: 'username',
                disabled: true,
                autoComplete: 'off',
            },
            password_changed: {
                type: 'text',
                placeholder: 'password changed',
                disabled: true,
            },
            modified: {
                type: 'text',
                placeholder: 'last modified',
                disabled: true,
            },
            created: {
                type: 'text',
                placeholder: 'created',
                disabled: true,
            },
            token_expires: {
                type: 'text',
                placeholder: 'Token expires',
                disabled: true,
            },
        }
    }

    save() {
        return super
            .save({ store: this.props.useStore, withEmailAddresses: true })
            .then(null, (response) => {
                alert(JSON.stringify(response))
            })
    }

    onChangeNotificationSetting(setting, e) {
        const { value, name } = e.target
        const { entity } = this.props
        const idA = Number(setting.notification_id)
        let updated = entity.notification_settings.map((oldSetting) => {
            if (Number(oldSetting.notification_id) === idA) {
                return { ...oldSetting, [name]: value }
            }
            return oldSetting
        })
        const hadEntry = updated.some((s) => Number(s.notification_id) === idA)
        if (!hadEntry) {
            updated = [
                ...updated,
                {
                    user_id: entity.id,
                    notification_id: setting.notification_id,
                    [name]: value,
                },
            ]
        }
        this.onChangeEntity({ notification_settings: updated })
    }

    getSetting(notificationId) {
        if (!this.notificationSettings.has(notificationId)) {
            const { entity } = this.props
            const setting = entity.notification_settings.find(
                (setting) => setting.notification_id === notificationId,
            )
            this.notificationSettings.set(
                notificationId,
                setting || {
                    user_id: entity.id,
                    notification_id: notificationId,
                    email: true,
                },
            )
        }
        return this.notificationSettings.get(notificationId)
    }

    componentDidMount() {
        const { entity } = this.props
        const { notificationIds } = this.props

        // Initialize missing notification settings
        if (notificationIds && entity.notification_settings) {
            const missingSettings = notificationIds.filter(
                (id) =>
                    !entity.notification_settings.some((setting) => setting.notification_id === id),
            )

            if (missingSettings.length > 0) {
                const newSettings = missingSettings.map((notificationId) => ({
                    user_id: entity.id,
                    notification_id: notificationId,
                    email: true, // if no email settings, consider them as subscribed (opt-out model)
                }))

                this.onChangeEntity({
                    notification_settings: [...entity.notification_settings, ...newSettings],
                })
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.entity.notification_settings !== this.props.entity.notification_settings) {
            this.notificationSettings.clear()
        }
    }

    onChangeAvatar(file) {
        const { avatarFile = {} } = this.props.entity
        this.onChangeEntity({
            avatarFile: { ...avatarFile, ...file },
        })
    }

    onCrop(crop, crop2) {
        const { avatarFile = {} } = this.props.entity
        this.onChangeEntity({
            avatarFile: { ...avatarFile, crop: { ...crop, aspect: 1 }, crop2 },
        })
    }

    validate() {
        const { entity } = this.props
        const emailAddressIds = entity.email_addresses || []
        let valid = super.validate()
        emailAddressIds.forEach(
            (emailAddressId) => (valid = this.emailAddressValidates[emailAddressId]() && valid),
        )
        return valid
    }

    render() {
        const {
            useStore,
            entity,
            entities,
            notifications,
            notificationIds,
            profileMode,
            hideSave,
            onFilesRejected,
        } = this.props
        const emailAddressIds = entity.email_addresses || []
        const { emailAddresses = {} } = entities

        const isTouched = () =>
            entity._isTouched ||
            emailAddressIds.some(
                (emailAddressId) => (emailAddresses[emailAddressId] || {})._isTouched,
            )

        if (profileMode) {
            return (
                <div>
                    {entity._saving && <LinearLoader />}
                    <Divider m />
                    <Row middle="xs">
                        <Col x>{this.renderInput('email')}</Col>
                    </Row>
                    <Divider m />
                    <Row middle="xs" style={{ flexWrap: 'nowrap' }}>
                        <Col x>{this.renderInput('first_name')}</Col>
                        <Col x>{this.renderInput('last_name')}</Col>
                    </Row>
                    <Divider m />
                    {!hideSave && (
                        <Row end="xs">
                            <Col x>
                                <Button disabled={!isTouched()} onClick={this.save}>
                                    {entity._saving ? 'saving..' : 'Save'}
                                </Button>
                            </Col>
                        </Row>
                    )}
                </div>
            )
        }

        return (
            <div>
                {entity._saving && <LinearLoader />}
                <Row middle="xs">
                    <Col x>
                        <AvatarEdit
                            onChangeImage={this.onChangeAvatar}
                            onCrop={this.onCrop}
                            size={50}
                            user={entity}
                            crop={(entity.avatarFile && entity.avatarFile.crop) || {}}
                            file={entity.avatarFile}
                            onFilesRejected={onFilesRejected}
                        />
                    </Col>
                    <Col x>{this.renderInput('email')}</Col>
                    <Col x>{this.renderInput('active')} </Col>
                    <Col x> {this.renderInput('country_code')} </Col>
                </Row>
                <Divider m />
                <Row middle="xs" style={{ flexWrap: 'nowrap' }}>
                    <Col x>{this.renderInput('gender')}</Col>
                    <Col x>{this.renderInput('first_name')} </Col>
                    <Col x>{this.renderInput('last_name')} </Col>
                </Row>
                <Divider m />
                <Row middle="xs" style={{ flexWrap: 'nowrap' }}>
                    <Col x>{this.renderInput('username')}</Col>
                    <Col x>{this.renderInput('phone')}</Col>
                </Row>
                <Divider m />
                <Row middle="xs" style={{ flexWrap: 'nowrap' }}>
                    <Col x>{this.renderInput('fc_whatsapp_enabled')}</Col>
                    <Col x>{this.renderInput('phone_notifications_display')}</Col>
                    <Col x>phone_notifications: {entity.phone_notifications}</Col>
                </Row>
                <Divider m />
                {this.renderInput('email_notifications')}
                {emailAddressIds.map((emailAddressId, i) => (
                    <Fragment key={emailAddressId}>
                        <Divider m />
                        <EmailAddressEdit
                            useStore={useStore}
                            entityId={emailAddressId}
                            addValidate={(validate) =>
                                (this.emailAddressValidates[emailAddressId] = validate)
                            }
                            num={i + 2}
                        />
                    </Fragment>
                ))}
                <Divider m />
                <Button
                    name="text"
                    onClick={() =>
                        this.addToRelation('email_addresses', { user_id: entity.id, email: '' })
                    }>
                    <PreIcon fontSize={24} name="add" primColor>
                        Add email notifications address
                    </PreIcon>
                </Button>
                <Divider m />
                <div className={style.notificationSettings}>
                    {notificationIds &&
                        entity.notification_settings &&
                        notificationIds.map((id) => {
                            const notification = notifications[id]
                            if (
                                notification.for_type ===
                                (entity.service_provider_id ? 'pro' : 'demand')
                            ) {
                                const setting = this.getSetting(id)
                                const isAutomaticReviewInviteMail =
                                    notification.name === 'automatic_review_invite_mail'
                                const adminPerm = window._user?.permissions?.admin
                                const hasAdminWrite =
                                    adminPerm &&
                                    (adminPerm.w === true ||
                                        adminPerm.w === 1 ||
                                        adminPerm._joinData?.w === true ||
                                        adminPerm._joinData?.w === 1)
                                const canEditAutomaticReviewInvite =
                                    isSuperUser() || !!hasAdminWrite
                                const disableAutomaticReviewInviteOnly =
                                    isAutomaticReviewInviteMail && !canEditAutomaticReviewInvite
                                return (
                                    <Fragment key={id}>
                                        <span className="cap">
                                            {notification.name.replace(/\_/g, ' ')}
                                        </span>
                                        <CheckButton
                                            name="email"
                                            msg="email"
                                            checked={setting.email}
                                            value={!setting.email}
                                            {...(disableAutomaticReviewInviteOnly
                                                ? { disabled: true }
                                                : {})}
                                            onChange={this.onChangeNotificationSetting.bind(
                                                this,
                                                setting,
                                            )}
                                        />
                                    </Fragment>
                                )
                            }
                            return null
                        })}
                </div>
                <Divider m />
                <Row>
                    <Col x>Last login:</Col>
                    <Col x>
                        {entity.last_login ? (
                            <FormattedTime date={entity.last_login} format="dd-MM-yyyy HH:mm:ss" />
                        ) : (
                            'Unknown'
                        )}
                    </Col>
                </Row>
                {(entity.users_identities || []).length > 0 && (
                    <>
                        <Divider s />
                        <Row middle="xs">
                            <Col x>Linked accounts:</Col>
                            <Col x>
                                <AutoRow>
                                    {entity.users_identities.map((identity) => (
                                        <span
                                            key={identity.id}
                                            className={clsx(
                                                SSO_BADGE[identity.provider] ||
                                                    'text-white bg-neutral-500',
                                                'inline-flex items-center rounded-lg px-2 py-1 text-md font-medium',
                                            )}
                                            title={identity.email_at_link || identity.provider}>
                                            {SSO_ICON[identity.provider]}
                                            {SSO_LABEL[identity.provider] || identity.provider}
                                        </span>
                                    ))}
                                </AutoRow>
                            </Col>
                        </Row>
                    </>
                )}
                <Divider m />
                <Restricted location="admin" checkWrite>
                    <Row>
                        <Col x>
                            <Button
                                name="logout_all_devices"
                                onClick={() => {
                                    if (
                                        window.confirm(
                                            'Log out all devices and revoke push notifications for this user? They will need to log in again on each device.',
                                        )
                                    ) {
                                        this.props
                                            .invalidateSessionForUser(entity.id)
                                            .then(() => {
                                                if (this.props.onInvalidateSessionSuccess) {
                                                    this.props.onInvalidateSessionSuccess()
                                                }
                                            })
                                            .catch(() => {})
                                    }
                                }}>
                                Log out all devices
                            </Button>
                        </Col>
                        <Col x>
                            <Button
                                name="invalidate_deep_link_tokens"
                                onClick={() => {
                                    if (
                                        window.confirm(
                                            'Revoke every outstanding deep-link / bearer JWT for this user? All existing email and HubSpot deep-links will stop working, and any active mobile session for this user will be logged out on the next request.',
                                        )
                                    ) {
                                        this.props
                                            .invalidateJwtForUser(entity.id)
                                            .then(() => {
                                                if (this.props.onInvalidateSessionSuccess) {
                                                    this.props.onInvalidateSessionSuccess()
                                                }
                                            })
                                            .catch(() => {})
                                    }
                                }}>
                                Revoke deep-link tokens
                            </Button>
                        </Col>
                    </Row>
                </Restricted>
                {(entity.users_tags || []).length > 0 && (
                    <>
                        <Divider m />
                        <UsersTags usersTags={entity.users_tags} />
                        <Divider m />
                    </>
                )}
                <Toggle
                    title="Show more fields"
                    showIcon
                    bottomToggle
                    extraTitleClasses={[style.showMoreFields]}>
                    <Divider m />
                    <Row>
                        <Col x>{this.renderInput('created')}</Col>
                        <Col x>{this.renderInput('token_expires')}</Col>
                    </Row>
                    <Divider m />
                    <Row>
                        <Col x>{this.renderInput('password_changed')}</Col>
                        <Col x>{this.renderInput('modified')}</Col>
                    </Row>
                </Toggle>
                <Divider m />
                {!hideSave && (
                    <Row end="xs">
                        <Col x>
                            <Button disabled={!isTouched()} onClick={this.save}>
                                {entity._saving ? 'saving..' : 'Save'}
                            </Button>
                        </Col>
                    </Row>
                )}
            </div>
        )
    }
}

UserEntity.propTypes = {}

export default EntityComponentContainer(UserEntity, {
    name: 'users',
    saveFunc: saveUser,
    mapDispatchToProps: (dispatch) => {
        return {
            onFilesRejected: (files) => dispatch(onFilesRejected(files)),
            invalidateSessionForUser: (userId) => dispatch(invalidateSessionForUser(userId)),
            invalidateJwtForUser: (userId) => dispatch(invalidateJwtForUser(userId)),
        }
    },
})
